import React from 'react';
import './cachemem.css';
import {Controlled as CodeMirrorComponent} from 'react-codemirror2';
import SolutionPanel from '../solutionpanel';
import { Icon, Button, NumericInput } from '@blueprintjs/core';
import StringSelect from '../stringselect';
import CacheTable from './cachetable';
import _ from 'lodash';
import ThemeContext from '../themecontext';
import SolutionVisualizer from './solutionvisualizer';

export default class Cachemem extends React.Component {

    constructor(props) {
        super(props);
        this.state = {  
                        addrWidth: 32,
                        cacheSize: 1024,
                        blockSize: 64,
                        assoc: 2,
                        addresses: "",
                        numOfAddresses: 6,
                        error: "",
                        content: [],
                        ages: []
                    };
        this.checkParamValidity = this.checkParamValidity.bind(this);
        this.generateAddresses = this.generateAddresses.bind(this);
        this.generateParams = this.generateParams.bind(this);
        this.generateCacheContent = this.generateCacheContent.bind(this);
    }

    componentDidMount() {
        this.generateParams();
    }

    getValidAddresses() {
        return this.state.addresses.split("\n").map(x => x.trim()).filter(x => /^(0|[1-9]\d*)$/.test(x));
    }

    generateParams() {
        const assoc = _.sample([1, 2, 4, 8]);
        let rows = _.sample([1, 2, 4, 8]);
        if (assoc * rows < 8)
            rows = 8 / assoc;
        const blockSize = _.sample([16, 32, 64, 128, 256]);
        const cacheSize = blockSize * assoc * rows;
        const addrWidth = Math.log2(cacheSize * _.sample([128, 256, 512, 1024, 2048, 4096, 8192, 16384, 32768, 65536, 131072, 262144]));

        this.setState({addrWidth, cacheSize, blockSize, assoc}, () => this.generateAddresses());
    }

    generateAddresses() {
        const addrParts = [];
        const indexNum = this.state.cacheSize / this.state.blockSize / this.state.assoc;
        const tagMaxNum = Math.pow(2, this.state.addrWidth - Math.log2(this.state.blockSize) - Math.log2(this.state.cacheSize / this.state.blockSize / this.state.assoc));
        const tagNum = Math.min(this.state.assoc*4, tagMaxNum);

        let ix = 0;
        // generate arbitrary addresses
        while (ix < this.state.numOfAddresses/2) {
            addrParts.push({ 
                tag: Math.floor(Math.random()*tagNum), 
                index: Math.floor(Math.random()*indexNum),
                offset: Math.floor(Math.random()*this.state.blockSize)
            });
            ix += 1;
        }
        // generate some addresses with matching index fields
        while (ix < this.state.numOfAddresses*3/4) {
            const model = _.sample(addrParts);
            addrParts.push({
                tag: Math.floor(Math.random()*tagNum), 
                index: model.index,
                offset: Math.floor(Math.random()*this.state.blockSize)
            });
            ix += 1;
        }
        // generate some addresses with matching index and tag fields (leading to cache hit)
        while (ix < this.state.numOfAddresses) {
            const model = _.sample(addrParts);
            addrParts.push({
                tag: model.tag, 
                index: model.index,
                offset: Math.floor(Math.random()*this.state.blockSize)
            });
            ix += 1;
        }
        // fill in the address array
        this.setState({addresses: addrParts.map(x => 
                x.offset + x.index * this.state.blockSize + x.tag * indexNum * this.state.blockSize
            ).join("\n")}, () => this.generateCacheContent());        
    }

    generateCacheContent() {

        const fltAddresses = this.getValidAddresses();

        const indexNum = this.state.cacheSize / this.state.blockSize / this.state.assoc;
        const content = [_.range(0, indexNum).map(ix => _.slice(_.shuffle(_.range(0, this.state.assoc * 4)), 0, this.state.assoc))];
        const ages = [_.range(0, indexNum).map(ix => _.shuffle(_.range(1, this.state.assoc+1)))];

        // create cache hits artificially
        _.slice(_.shuffle(fltAddresses), 0, Math.floor(0.25*fltAddresses.length)).forEach(addr => {
            const blockNum = Math.floor(addr / this.state.blockSize);
            const index = blockNum % indexNum;
            const tag = Math.floor(blockNum / indexNum);
            const col = Math.floor(Math.random() * this.state.assoc);
            if (content[0][index].indexOf(tag) < 0)
                content[0][index][col] = tag;
        });

        // simulate cache behavior
        for (let step=0; step < fltAddresses.length; step++) {
            // start from the old content
            content[step+1] = _.cloneDeep(content[step]);
            ages[step+1] = _.cloneDeep(ages[step]);
            // take new address
            const addr = fltAddresses[step];
            const blockNum = Math.floor(addr / this.state.blockSize);
            const index = blockNum % indexNum;
            const tag = Math.floor(blockNum / indexNum);
            // find tag and replace if necessary
            let col = content[step+1][index].indexOf(tag);
            if (col < 0)
                col = ages[step+1][index].indexOf(this.state.assoc);
            content[step+1][index][col] = tag;
            // update age fields
            ages[step+1][index] = ages[step][index].map(a => a < ages[step][index][col] ? a+1 : a);
            ages[step+1][index][col] = 1;
        }

        this.setState({content, ages});
    }

    checkParamValidity() {
        let error = "";
        if (Math.log2(this.state.cacheSize) > this.state.addrWidth)
            error = "Nagyobb a cache méret, mint a rendszermemória!";
        else if (this.state.cacheSize % this.state.blockSize !== 0)
            error = "A cache méret nem egész számú többszöröse a blokkméretnek!";
        else {
            const blockNum = this.state.cacheSize / this.state.blockSize;
            if (blockNum < 4) 
                error = "A cache blokkok száma 4-nél kevesebb!";
            else if (blockNum % this.state.assoc)
                error = "A blokkok száma nem egész számú többszöröse a cache asszociativitásának!";
        }
        this.setState({error});
    }

    renderAddressComponents() {
        const fltAddresses = this.getValidAddresses();
        if (fltAddresses.length < 1)
            return <p>Egyetlen memóriacím sincs megadva!</p>;
        else {
            const indexNum = this.state.cacheSize / this.state.blockSize / this.state.assoc;
            return <div className="cachemem-addresscomp-table-container"><table className="cachemem-addresscomp-table">
                <thead>
                    <tr><th></th>{fltAddresses.map((a,ix) => <th key={ix}>{a}</th>)}</tr>
                </thead>
                <tbody>
                <tr><td>Blokk száma:</td>{fltAddresses.map((a,ix) => <td key={ix}>{Math.floor(a/this.state.blockSize)}</td>)}</tr>
                <tr><td>Offset:</td>{fltAddresses.map((a,ix) => <td key={ix}>{a % this.state.blockSize}</td>)}</tr>
                <tr><td>Index:</td>{fltAddresses.map((a,ix) => <td key={ix}>{Math.floor(a/this.state.blockSize) % indexNum}</td>)}</tr>
                <tr><td>Tag:</td>{fltAddresses.map((a,ix) => <td key={ix}>{Math.floor(Math.floor(a/this.state.blockSize) / indexNum)}</td>)}</tr>
                </tbody>
            </table></div>
        }
    }

    renderBasicProps() {
        const strokeProps = {
            stroke: getComputedStyle(document.querySelector('body')).getPropertyValue("--main-fg-color"),
            fill: getComputedStyle(document.querySelector('body')).getPropertyValue("--main-fg-color")
        };
        const fontProps = {
            fontSize: "14px",
            fill: getComputedStyle(document.querySelector('body')).getPropertyValue("--main-fg-color")
        };
        return <div className="cachemem-multi-line">
            <p>
                Az offset mező bitszélességét a blokkméret kettes alapú logaritmusa adja, ennyi bittel lehet végigcímezni egy blokkot.
                Ebben a példában ez log<sub>2</sub>({this.state.blockSize}) = <span className="demo-solution-value">{Math.log2(this.state.blockSize)}</span> bit.
            </p>
            <p>
                Az index mező bitszélességének kiszámításához először meg kell határoznunk, hogy a cache-be hány blokk fér.
                A megadott paraméterek mellett cache méret / blokkméret = <span className="demo-solution-value">{this.state.cacheSize / this.state.blockSize}</span> blokk fér a cache-be.
                Mivel az asszociativitás {this.state.assoc}, egyazon indexű blokkok {this.state.assoc} helyre tehetők a cache-ben, amiből következik, hogy
                cache méret / blokkméret / asszociativitás = {this.state.cacheSize / this.state.blockSize / this.state.assoc} különböző indexű blokkot tud a cache tárolni.
                Ennyi lehetőség címzéséhez log<sub>2</sub>({this.state.cacheSize / this.state.blockSize / this.state.assoc}) = <span className="demo-solution-value">{Math.log2(this.state.cacheSize / this.state.blockSize / this.state.assoc)}</span> 
                bitre van szükség, ez lesz az index mező címszélessége. 
            </p>
            <p>
                A címből fennmaradó {this.state.addrWidth} - {Math.log2(this.state.blockSize)} - {Math.log2(this.state.cacheSize / this.state.blockSize / this.state.assoc)} = 
                <span className="demo-solution-value">{this.state.addrWidth - Math.log2(this.state.blockSize) - Math.log2(this.state.cacheSize / this.state.blockSize / this.state.assoc)}</span> bitje
                pedig cache tagként fog szolgálni.
            </p>
            <p>
                Ennek megfelelően a címek tagozódása:
            </p>
            <p>
                <svg width="250" height="92px">
                    <defs>
                    <marker id="arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                        <path d="M 0 0 L 10 5 L 0 10 z" {...strokeProps} />
                    </marker>
                    </defs>
                    <text x="125px" y="9px" textAnchor="middle" dominantBaseline="middle" {...fontProps} >
                        Teljes cím, {this.state.addrWidth} bit
                    </text>
                    <line x1="2px" y1="16px" x2="248px" y2="16px" {...strokeProps} markerEnd='url(#arrow)' markerStart='url(#arrow)'/>
                    <rect y="24px" x="2px" width="248px" height="22px" {...strokeProps} fill="transparent" />
                    <text x="220px" y="36px" textAnchor="middle" dominantBaseline="middle" {...fontProps} >
                        offset
                    </text>
                    <line x1="193px" y1="54px" x2="248px" y2="54px" {...strokeProps} markerEnd='url(#arrow)' markerStart='url(#arrow)'/>
                    <text x="220px" y="65px" textAnchor="middle" dominantBaseline="middle" {...fontProps} >
                        {Math.log2(this.state.blockSize)} bit
                    </text>

                    <line x1="192px" y1="24px" x2="192px" y2="46px" {...strokeProps}/>
                    <text x="165px" y="36px" textAnchor="middle" dominantBaseline="middle" {...fontProps} >
                        index
                    </text>
                    <line x1="139px" y1="54px" x2="191px" y2="54px" {...strokeProps} markerEnd='url(#arrow)' markerStart='url(#arrow)'/>
                    <text x="165" y="65px" textAnchor="middle" dominantBaseline="middle" {...fontProps} >
                        {Math.log2(this.state.cacheSize / this.state.blockSize / this.state.assoc)} bit
                    </text>

                    <line x1="138px" y1="24px" x2="138px" y2="46px" {...strokeProps}/>
                    <text x="68px" y="36px" textAnchor="middle" dominantBaseline="middle" {...fontProps} >
                        tag
                    </text>
                    <line x1="2px" y1="54px" x2="137px" y2="54px" {...strokeProps} markerEnd='url(#arrow)' markerStart='url(#arrow)'/>
                    <text x="68" y="65px" textAnchor="middle" dominantBaseline="middle" {...fontProps} >
                        {this.state.addrWidth - Math.log2(this.state.blockSize) - Math.log2(this.state.cacheSize / this.state.blockSize / this.state.assoc)} bit
                    </text>
                    <line x1="2px" y1="76px" x2="191px" y2="76px" {...strokeProps} markerEnd='url(#arrow)' markerStart='url(#arrow)'/>
                    <text x="97px" y="87px" textAnchor="middle" dominantBaseline="middle" {...fontProps} >
                        Blokkszám, {this.state.addrWidth - Math.log2(this.state.blockSize)} bit
                    </text>
                    
                </svg>
            </p>
            <p>
                A cache a keresések során az azonos indexhez tartozó blokkok tag mezőjét hasonlítja össze. A cache asszociativitása
                {this.state.assoc}, azaz ennyi azonos indexű blokkot tud tárolni, vagyis ennyi összehasonlítást kell végezni minden kereséskor.
                A tagek összehasonlításához pedig <span className="demo-solution-value">{this.state.addrWidth - Math.log2(this.state.blockSize) - Math.log2(this.state.cacheSize / this.state.blockSize / this.state.assoc)}</span> bites 
                összehasonlításra van szükség.
            </p>
            </div>
    }


    static contextType = ThemeContext;

    render() {

        let error = <></>;
        if (this.state.error.length > 0) {
            error = <div className="code-error-area">
                <Icon icon="error"/>&nbsp;{this.state.error}
            </div>
        }
        const indexNum = this.state.cacheSize / this.state.blockSize / this.state.assoc;

        return (
            <>
                <div className="title-area">
                    <h1>Cache memória</h1>
                    <h2>Szervezés és tartalom menedzsment</h2>
                </div>
                <div className="demo-emph-row">
                    <div className="cachemem-param-panel">
                        <h3>Paraméterek:</h3>
                        <div className="cachemem-param-grid">
                            <div className="cachemem-param-label">A memóriacímek szélessége:</div>
                            <div className="cachemem-param-row">
                                <NumericInput 
                                    className="cachemem-param-numeric"
                                    fill="true"
                                    value={this.state.addrWidth}
                                    onValueChange={val => this.setState({addrWidth: val}, () => {
                                        this.checkParamValidity();
                                        this.generateAddresses();
                                    })}
                                    min={12}
                                    max={64}
                                /> [bit]
                            </div>
                            <div className="cachemem-param-label">A cache memória mérete:</div>
                            <div className="cachemem-param-row">
                                <NumericInput 
                                    className="cachemem-param-numeric"
                                    fill="true"
                                    value={this.state.cacheSize}
                                    onValueChange={val => this.setState({cacheSize: val}, () => {
                                        this.checkParamValidity();
                                        this.generateAddresses();
                                    })}
                                    min={128}
                                    max={65536}
                                /> [byte]
                            </div>                            
                            <div className="cachemem-param-label">A cache blokkok mérete:</div>
                            <div className="cachemem-param-row">
                                <StringSelect
                                    className="cachemem-param-numeric"
                                    items={["16", "32", "64", "128", "256"]}
                                    initial={"" + this.state.blockSize}
                                    onChange={val => this.setState({blockSize: +val}, () => {
                                        this.checkParamValidity();
                                        this.generateAddresses();
                                    })}
                                />
                                <div className="cachemem-param-unit">[byte]</div>
                            </div>                            
                            <div className="cachemem-param-label">A cache memória asszociativitása:</div>
                            <div className="cachemem-param-long-row">
                                <StringSelect
                                    items={["1 (direkt leképzés)", "2", "4", "8", "Teljesen asszociatív"]}
                                    initial={this.state.assoc === 1 ? "1 (direkt leképzés)" : (this.state.assoc === this.state.cacheSize / this.state.blockSize ? "Teljesen asszociatív" : "" + this.state.assoc)}
                                    onChange={val => {
                                        let assoc;
                                        if (val === "1 (direkt leképzés)")
                                            assoc = 1;
                                        else if (val === "Teljesen asszociatív")
                                            assoc = this.state.cacheSize / this.state.blockSize;
                                        else
                                            assoc = +val;
                                        this.setState({assoc: assoc}, () => {
                                            this.checkParamValidity();
                                            this.generateAddresses();
                                        });
                                    }}
                                />
                            </div>                            
                        </div>
                        <Button 
                            icon="refresh"
                            text="Random generálás"
                            className="cachemem-generate-button"
                            onClick={this.generateParams} 
                        />
                    </div>
                </div>
                {error}
                <div>
                    <h3>1. Feladat</h3>
                    <p>Adja meg, hány blokk fér a cache memóriába! Hány bites az offset, a cache index és a cache tag? Hány azonos indexű
                        blokkot képes tárolni a cache? Hány tag összehasonlítását kell elvégezni minden egyes cache keresés során?
                        Hány bit szélesek ezek az összehasonlítások?
                    </p>
                </div>
                <div>
                    <SolutionPanel title="Megoldás mutatása">
                        <div className="demo-emph-row">
                            {this.renderBasicProps()}
                        </div>
                    </SolutionPanel>
                </div>
                <div>
                    <h3>2. Feladat</h3>
                    <p> A futó program az alábbi, jobb oldalon látható memóriacímekről olvas adatokat, a megadott sorrendben. A cache memória kezdeti tartalmát az alábbi
                        ábra bal oldala mutatja. A cache memóriában az egymást követő sorok az egymást követő indexekhez tartoznak (0-tól számozva), és a cache 
                        asszociativitása határozza meg, hogy hány azonos méretű blokkot képes tárolni (az oszlopok száma ebből adódik). A táblázat celláiba az ott
                        tárolt adat "tag" mezője van feltüntetve, a cella jobb felső sarkában látható kis szám pedig arra utal, hogy milyen régen hivatkozták ezt az adatot.
                        Ha ez a "kor" mező "1', akkor ez a legutóbb hivatkozott blokk, és minél nagyobb a "kor" mező tartalma, annál régebben hivatkozták az adatot. A "kor" 
                        mező nem abszolút időt, csak egy relatív sorrendet definiál. A "kor" mező direkt leképzés esetén nem játszik szerepet, hiányzik az ábráról is.
                    </p>
                    <div className="cachemem-sequence-params">
                        <div className="cachemem-emph-area">
                            <div className="cachemem-emph-header">A hivatkozott memóriacímek:</div>
                            <div className="cachemem-sequence-editor">
                                <CodeMirrorComponent
                                    value={this.state.addresses}
                                    options={{
                                        theme: this.context === "dark" ? "material" : "default",
                                        lineNumbers: true}}
                                    onBeforeChange={(editor, data, value) => {
                                        this.setState({addresses: value}, () => {
                                            this.checkParamValidity();
                                            this.generateCacheContent();
                                        });
                                    }}                                        
                                />                           
                            </div>
                            <div className="cachemem-sequence-bottom-panel">
                                <Button 
                                    icon="refresh" 
                                    text="Random"
                                    className="cachemem-refresh-sequence"
                                    onClick={() => this.generateAddresses()} 
                                />
                                <NumericInput 
                                    className="cachemem-sequence-num"
                                    fill="true"
                                    value={this.state.numOfAddresses}
                                    onValueChange={val => this.setState({numOfAddresses: val})}
                                    min={4}
                                    max={16}
                                /> 
                                <div>cím</div>
                            </div>
                        </div>
                        <div className="cachemem-emph-area">
                            <div className="cachemem-emph-header">A cache memória kezdeti állapota:</div>
                            <CacheTable content={this.state.content} ages={this.state.ages} step={0} />
                            <Button 
                                icon="refresh" 
                                minimal="true" 
                                className="cachemem-refresh-cache"
                                onClick={() => this.generateCacheContent()} 
                            />
                        </div>
                    </div>
                </div>
                <div>
                    <h3>a) feladat: </h3>
                    <p>
                        Adja meg minden egyes memóriacímre a blokk memóriabeli sorszámát, valamint az offset, az index és a tag mezők tartalmát!
                    </p>
                </div>
                <div>
                    <SolutionPanel title="Megoldás mutatása">
                        <div className="cachemem-sequence-params">
                            <div className="cachemem-emph-area">
                                {this.renderAddressComponents()}
                            </div>
                        </div>
                    </SolutionPanel>
                </div>                
                <div>
                    <h3>b) feladat: </h3>
                    <p>
                        Adja meg cache tartalmának alakulását az egyes memóriahibvatkozások után! Vegye figyelembe, hogy a memóriahivatkozások sorrendje számít!
                        Minden lépés után jegyezze fel, hogy cache találat ("hit"), vagy cache hiba ("miss") történt-e!
                    </p>
                </div>
                <div>
                    <SolutionPanel title="Megoldás mutatása">                        
                        {this.state.content.length>0 && <div className="cachemem-sequence-params">
                            <div className="cachemem-emph-area">
                                <SolutionVisualizer 
                                    content={this.state.content}
                                    ages={this.state.ages}
                                    addresses={this.getValidAddresses().map(addr => {
                                        return {address: addr, index: Math.floor(addr/this.state.blockSize) % indexNum, tag: Math.floor(Math.floor(addr/this.state.blockSize) / indexNum)}
                                    })}
                                />
                            </div>
                        </div>}
                    </SolutionPanel>
                </div>                
            </>
        );
    }
}